
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertCircle, Facebook, RefreshCw, LinkIcon, Search, UserPlus } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { MetaConnectDialog } from '@/components/integrations/meta-connect';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getMetaAdAccounts } from '@/utils/metaApi/accounts';

interface Customer {
  id: string;
  name: string;
  email: string;
  status: string;
}

interface MetaAccount {
  id: string;
  name: string;
  status?: number;
}

interface MetaConnection {
  id: string;
  customer_id: string;
  ad_account_id: string;
  account_name: string;
  customer_name: string;
  customer_email: string;
  created_at: string;
}

export const MetaAccountsManager: React.FC = () => {
  const [isConnectDialogOpen, setIsConnectDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [metaAccounts, setMetaAccounts] = useState<MetaAccount[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [connections, setConnections] = useState<MetaConnection[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [isAssigning, setIsAssigning] = useState(false);
  const { toast } = useToast();
  
  // Fetch customers
  const fetchCustomers = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const { data, error } = await supabase
        .from('customers')
        .select('id, name, email, status')
        .order('name');
      
      if (error) throw error;
      
      setCustomers(data || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
      setError('Fehler beim Laden der Kundendaten');
      toast({
        title: "Fehler beim Laden der Kundendaten",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fetch Meta connections
  const fetchConnections = async () => {
    try {
      setIsLoading(true);
      
      // Join meta_connections with customers to get customer info
      const { data, error } = await supabase
        .from('meta_connections')
        .select(`
          id,
          customer_id,
          ad_account_id,
          account_name,
          created_at,
          customers!meta_connections_customer_id_fkey (
            name,
            email
          )
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Transform the joined data into our format
      const formattedConnections = data?.map(item => ({
        id: item.id,
        customer_id: item.customer_id,
        ad_account_id: item.ad_account_id,
        account_name: item.account_name,
        created_at: item.created_at,
        customer_name: item.customers?.name || 'Unknown',
        customer_email: item.customers?.email || 'Unknown'
      })) || [];
      
      setConnections(formattedConnections);
    } catch (error) {
      console.error('Error fetching Meta connections:', error);
      setError('Fehler beim Laden der Meta-Verbindungen');
      toast({
        title: "Fehler beim Laden der Meta-Verbindungen",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fetch Meta accounts using stored token
  const fetchMetaAccounts = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // In a real app, we'd get the token from the database
      // For now, we'll simulate some accounts
      const mockAccounts = [
        { id: 'act_123456789', name: 'Test Ad Account 1' },
        { id: 'act_987654321', name: 'Test Ad Account 2' },
        { id: 'act_567891234', name: 'Test Ad Account 3' }
      ];
      
      // Uncomment this to use real Meta API when we have a token
      // const { data, error } = await supabase
      //   .from('admin_settings')
      //   .select('meta_access_token')
      //   .single();
      // 
      // if (error) throw error;
      // if (data?.meta_access_token) {
      //   const accounts = await getMetaAdAccounts(data.meta_access_token);
      //   setMetaAccounts(accounts);
      // }
      
      // For demo, use mock accounts
      setMetaAccounts(mockAccounts);
    } catch (error) {
      console.error('Error fetching Meta accounts:', error);
      setError('Fehler beim Laden der Meta-Konten');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Load data on component mount
  useEffect(() => {
    fetchCustomers();
    fetchConnections();
    fetchMetaAccounts();
  }, []);
  
  // Handle Meta OAuth success
  const handleMetaConnectSuccess = (token: string) => {
    toast({
      title: "Meta-Verbindung erfolgreich",
      description: "Sie können jetzt Meta-Werbekonten zuweisen",
    });
    
    // In a real app, we'd save this token in the database
    console.log("Meta access token:", token);
    
    // Close the dialog
    setIsConnectDialogOpen(false);
    
    // Reload accounts
    fetchMetaAccounts();
  };
  
  // Assign Meta account to customer
  const handleAssignAccount = async () => {
    if (!selectedAccountId || !selectedCustomerId) {
      toast({
        title: "Fehler",
        description: "Bitte wählen Sie ein Werbekonto und einen Kunden aus",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setIsAssigning(true);
      
      // Find the selected account and customer
      const account = metaAccounts.find(a => a.id === selectedAccountId);
      const customer = customers.find(c => c.id === selectedCustomerId);
      
      if (!account || !customer) {
        throw new Error("Ausgewähltes Konto oder Kunde nicht gefunden");
      }
      
      // Insert the connection
      const { error } = await supabase
        .from('meta_connections')
        .insert({
          customer_id: customer.id,
          user_id: customer.id, // For backward compatibility
          ad_account_id: account.id,
          account_name: account.name,
          access_token: "sample-token-for-demo", // In real app, use the actual token
          is_connected: true,
          assigned_by_admin_id: (await supabase.auth.getUser()).data.user?.id
        });
      
      if (error) throw error;
      
      toast({
        title: "Zuweisung erfolgreich",
        description: `Meta-Konto "${account.name}" wurde ${customer.name} zugewiesen`,
      });
      
      // Reset selections and reload connections
      setSelectedAccountId(null);
      setSelectedCustomerId(null);
      fetchConnections();
    } catch (error) {
      console.error('Error assigning Meta account:', error);
      toast({
        title: "Fehler bei der Zuweisung",
        description: error instanceof Error ? error.message : "Unbekannter Fehler",
        variant: "destructive",
      });
    } finally {
      setIsAssigning(false);
    }
  };
  
  // Filter customers by search query
  const filteredCustomers = customers.filter(customer => 
    customer.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
    customer.email.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold tracking-tight">Meta-Konten Manager</h1>
        <Button 
          onClick={() => setIsConnectDialogOpen(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Facebook className="mr-2 h-4 w-4" />
          Mit Meta verbinden
        </Button>
      </div>
      
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Fehler</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      <Card>
        <CardHeader>
          <CardTitle>Meta-Verbindung einrichten</CardTitle>
          <CardDescription>
            Verbinden Sie Ihr Meta-Konto, um Werbekonten zuzuweisen
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-1">Meta-Werbekonto</label>
                <Select
                  value={selectedAccountId || ''}
                  onValueChange={setSelectedAccountId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Werbekonto auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {metaAccounts.map(account => (
                      <SelectItem key={account.id} value={account.id}>
                        {account.name} ({account.id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Kunde</label>
                <Select
                  value={selectedCustomerId || ''}
                  onValueChange={setSelectedCustomerId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Kunde auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredCustomers.map(customer => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.name} ({customer.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex justify-end">
              <Button 
                onClick={handleAssignAccount}
                disabled={!selectedAccountId || !selectedCustomerId || isAssigning}
              >
                {isAssigning ? (
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <LinkIcon className="mr-2 h-4 w-4" />
                )}
                Zuweisen
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle>Bestehende Verbindungen</CardTitle>
          <CardDescription>
            Liste der zugewiesenen Werbekonten
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <div className="flex gap-2">
              <Input
                placeholder="Kunden suchen..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="max-w-sm"
              />
              <Button variant="outline" size="icon">
                <Search className="h-4 w-4" />
              </Button>
              <Button 
                variant="outline" 
                size="icon"
                onClick={fetchConnections}
                disabled={isLoading}
              >
                <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
          
          {connections.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Kunde</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Werbekonto</TableHead>
                  <TableHead>Konto-ID</TableHead>
                  <TableHead>Zugewiesen am</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {connections.filter(conn => 
                  conn.customer_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  conn.customer_email.toLowerCase().includes(searchQuery.toLowerCase())
                ).map((connection) => (
                  <TableRow key={connection.id}>
                    <TableCell className="font-medium">{connection.customer_name}</TableCell>
                    <TableCell>{connection.customer_email}</TableCell>
                    <TableCell>{connection.account_name}</TableCell>
                    <TableCell className="font-mono text-xs">{connection.ad_account_id}</TableCell>
                    <TableCell>{new Date(connection.created_at).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              {isLoading ? (
                <div className="flex flex-col items-center">
                  <RefreshCw className="h-8 w-8 animate-spin mb-2" />
                  <p>Daten werden geladen...</p>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <UserPlus className="h-8 w-8 mb-2 opacity-50" />
                  <p>Keine Verbindungen gefunden</p>
                  <p className="text-sm">Weisen Sie zuerst ein Werbekonto einem Kunden zu.</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      
      <MetaConnectDialog 
        open={isConnectDialogOpen} 
        onOpenChange={setIsConnectDialogOpen}
        onSuccess={handleMetaConnectSuccess}
      />
    </div>
  );
};
